import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../ts/Glob';
import * as permissions from '../../../_common/platform/ts/permissions';
import { config } from '../ts/config';
import ItemActionsSelector, { IActionButton } from './menus/ItemActionsSelector';
import BooleanField from '../../../_common/platform/tsx/BooleanField';

import {
    IDraggableItem,
    IDraggableItemData,
    handleMenuItemDragging
} from '../../../_common/platform/ts/DragDropCommon';

import { MenuModes } from '../tsx/menus/Common';

interface IDirsWorkspaceItemProperties {
    wsid: number;
    name: string;
    instanceID?: string;
    favouritesOrigin?: boolean;
    changingWorkspaces?: { [wsid: number]: boolean };
    displayDirId?: string | number;
    overwrite?: boolean;
    isFavourite?: boolean;
    dragDropData?: IDraggableItemData;
    disableMenu?: boolean;
    allowEditing?: boolean;
    markMode?: boolean;
    marked?: boolean;
    hideIcons: boolean;
    menuMode: MenuModes;
    glob: IGlob;

    onSelect?(id: number): void;
    onShowActions?(): void;
    onHideActions?(): void;
    onMarkedChange?(value: boolean): void;
}

export default function DirsWorkspaceItem(props: IDirsWorkspaceItemProperties) {
    const editFieldRef = React.useRef<HTMLInputElement>(null);
    const draggableItemRef = React.useRef<HTMLDivElement>(null);

    const [nameEditing, setNameEditing] = React.useState<string>();

    React.useEffect(() => {
        const getDragHelper = () => {
            const clone = $(draggableItemRef.current).clone();

            clone.removeAttr('data-reactid');

            clone.find('*').each(function () {
                $(this).removeAttr('data-reactid');
            });

            return clone;
        };

        const handleDragStart = (e, ui) => {
            $(draggableItemRef.current).addClass('dragging');
        };

        const handleDragStop = (e, ui) => {
            $(draggableItemRef.current).removeClass('dragging');
        };

        if (props.dragDropData != null) {
            $(draggableItemRef.current)
                .draggable({
                    helper: getDragHelper,

                    revert: true,
                    revertDuration: 0,
                    zIndex: 6,
                    addClasses: false,
                    appendTo: $('.menu-tree'),

                    start: handleDragStart,
                    stop: handleDragStop,

                    drag: handleMenuItemDragging,

                    scope: props.dragDropData.scope
                })
                .data('payload', {
                    itemType: props.dragDropData.itemType,
                    id: props.wsid,
                    name: props.name
                } as IDraggableItem);
        }
    }, []);

    React.useEffect(() => {
        if (nameEditing === props.name) {
            const input = editFieldRef.current;

            input.focus();
        }
    }, [props.name, nameEditing]);

    const handleOnClick = (e) => {
        if (typeof props.onSelect === 'function') {
            props.onSelect(props.wsid);
        }

        //Stopping propagation here prevents horizontal menu be closed when selecting the current workspace.
        // e.stopPropagation();
        // e.preventDefault();
    };

    const handleOnRenameChange = (e) => {
        setNameEditing(e.target.value);
    };

    const handleOnRenameEnd = (e) => {
        let newName = nameEditing;

        e.preventDefault();

        if (newName == null || newName.trim().length === 0) {
            //LL: on Enter: target=form, type="submit";  - OK with stopPropagation, remains in edit
            //LL: on LostFocus: target=input, type="blur" - No, so set focus again
            //LL: another solution may be to save initial name in start edit
            //console.log("handleRenameEnd: empty new name: type=" + e.type);
            props.glob.AppActions.showNotification_Fail(
                'Empty or Whitespace new name for Workspace is prohibited (event type=' + e.type + ')'
            );

            e.stopPropagation();

            if (e.type === 'blur') {
                e.currentTarget.focus();
            }
            return;
        }

        props.glob.AppActions.workspaceDirsRename(props.wsid, nameEditing, props.instanceID, props.favouritesOrigin);

        setNameEditing(null);
    };

    const handleRenameClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const handleOnActionsShow = () => {
        if (props.onShowActions != null) {
            props.onShowActions();
        }
    };

    const handleOnActionsClose = () => {
        if (props.onHideActions != null) {
            props.onHideActions();
        }
    };

    const changingStarted = () => {
        return props.changingWorkspaces != null && props.changingWorkspaces[props.wsid];
    };

    const processDelete = () => {
        if (changingStarted() || nameEditing != null) {
            return;
        }

        props.glob.DialogActions.createDialog(
            'Delete workspace "' + props.name + '"',
            'Workspace "' + props.name + '" will be deleted. Please, confirm this action.',
            () => {},
            () => {
                props.glob.AppActions.workspaceRemove(props.wsid, props.instanceID);
            },
            () => {},
            'confirm',
            {
                confirmTitle: null,
                cancelTitle: 'Delete'
            }
        );
    };

    const processMove = () => {
        if (changingStarted() || nameEditing != null || typeof props.displayDirId !== 'number') {
            return;
        }

        props.glob.DialogActions.createDialog(
            'Select Target Folder for Workspace "' + props.name + '"',
            '',
            (dirid, unVarNotUsed, overwrite) => {
                props.glob.AppActions.workspaceMove(
                    props.wsid,
                    props.displayDirId as number,
                    dirid,
                    props.instanceID,
                    overwrite
                );
            },
            () => {},
            () => {},
            'workspaceDirSelect',
            {
                selectTitle: 'Move to selected folder',
                rootId: config.WIDGETS_ROOT_DIR_ID,
                overwrite: props.overwrite || false, //VR add overwrite here,
                overwriteShow: true,
                name: props.name,
                allowSelectItem: false
                //dir: props.displayDirId = todo: VR: We should pass also 'wsPathDirId' and 'dir' to show the folder where this WS placed
            }
        );
    };

    const handleOnActionClick = (key: string) => {
        switch (key) {
            case 'rename':
                if (!changingStarted()) {
                    setNameEditing(props.name);
                }
                break;

            case 'move':
                processMove();
                break;

            case 'delete':
                processDelete();
                break;

            case 'favourite-add':
                props.glob.AppActions.workspaceFavouritesAdd(props.wsid, props.name, props.instanceID);
                break;

            case 'favourite-remove':
                props.glob.AppActions.workspaceFavouritesRemove(props.wsid, props.instanceID);
                break;

            default:
                console.log('WARNING: Menu Action has no handler.');
        }
    };

    const getActionButtons = (): IActionButton[] => {
        let result: IActionButton[] = [];

        if (props.allowEditing !== false && permissions.canEdit()) {
            result = result.concat([
                {
                    key: 'rename',
                    title: 'Rename',
                    icon: 'glyphicon glyphicon-pencil'
                },
                {
                    key: 'move',
                    title: 'Move',
                    icon: 'glyphicon glyphicon-transfer'
                },
                {
                    key: 'delete',
                    title: 'Delete',
                    icon: 'glyphicon glyphicon-remove'
                }
            ]);
        }

        if (permissions.showSideBarFavourites()) {
            if (result.length > 0) {
                result.push(null); //Divider.
            }

            if (props.isFavourite) {
                result.push({
                    key: 'favourite-remove',
                    title: 'Remove from favourites',
                    icon: 'glyphicon glyphicon-star-empty'
                });
            } else {
                result.push({
                    key: 'favourite-add',
                    title: 'Add to favourites',
                    icon: 'glyphicon glyphicon-star'
                });
            }
        }

        return result;
    };

    let markCell: React.JSX.Element;

    if (props.markMode) {
        markCell = (
            <td className="cell-mark">
                <BooleanField value={props.marked} title={''} onChange={props.onMarkedChange} />
            </td>
        );
    }

    let title;

    if (nameEditing != null && !changingStarted()) {
        title = (
            <form onSubmit={handleOnRenameEnd}>
                <input
                    ref={editFieldRef}
                    defaultValue={nameEditing}
                    onClick={handleRenameClick}
                    onInput={handleOnRenameChange}
                    onBlur={handleOnRenameEnd}
                />
            </form>
        );
    } else {
        title = props.name;
    }

    let actionSelector: React.JSX.Element;

    if (props.disableMenu !== true && (permissions.canEdit() || permissions.showSideBarFavourites())) {
        actionSelector = (
            <ItemActionsSelector
                className="config-button"
                buttons={getActionButtons()}
                width={200}
                glob={props.glob}
                onActionClick={handleOnActionClick}
                onShow={handleOnActionsShow}
                onClose={handleOnActionsClose}
            />
        );
    }

    let iconElement: React.JSX.Element;

    if (!props.hideIcons) {
        iconElement = (
            <td className="cell-icon">
                <span className="glyphicon glyphicon-th-large"></span>
            </td>
        );
    }

    return (
        <div
            key={props.wsid}
            className={
                'dir-node-flat dropdown dirs-content-item' +
                (changingStarted() ? ' loading' : '') +
                (props.isFavourite ? ' favourite' : '')
            }
        >
            <div
                className={'dir-node-flat-item' + (props.dragDropData != null ? ' dragabble' : '')}
                ref={draggableItemRef}
                onClick={handleOnClick}
            >
                <table className="dir-node-flat-table">
                    <tbody>
                        <tr>
                            {markCell}
                            {iconElement}

                            <td className="cell-title">{title}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {actionSelector}
        </div>
    );
}
